import { useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';

/**
 * Resolve the effective `clientId` for the current page.
 *
 * Two callers reach the same client-scoped routes (`/check-in`,
 * `/tools/<key>`) and they each know the clientId differently:
 *
 * - **Coach navigation** — opens the page from the client detail
 *   panel and passes `?clientId=<id>` in the URL. Surfaces the
 *   selected client's data regardless of which user is signed in.
 * - **Client navigation** — the client logs in and clicks into their
 *   own dashboard. There is no URL param; the clientId lives on the
 *   authenticated user's profile (`/users/me` returns `client_id` for
 *   client-role users, which `fetchProfile()` maps to `user.clientId`).
 *
 * Before this hook existed, every route hard-coded
 * `params.get('clientId') ?? ''` and surfaced "Klienten-ID fehlt" the
 * moment a client navigated in without the param. The hook now
 * prefers the URL value (so coach navigation keeps working unchanged)
 * and falls back to the signed-in user's own clientId.
 *
 * Returns `''` when neither source resolves a value — the page can
 * then render its "missing client" empty state (e.g. a coach opens
 * `/check-in` directly without selecting a client first).
 */
export function useClientId(): string {
  const [params] = useSearchParams();
  const { user } = useAuth();
  return params.get('clientId') ?? user?.clientId ?? '';
}
